// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

 
import XngZone from "@conklin/App/Components/Common/XngZone";
import useRowOverlap from "@conklin/App/Hooks/useRowOverlap";
import { CustomFieldDefinition, HorizontalAlignment } from "@conklin/CustomFieldDefinition";
import { GridContainerProps } from "@conklin/MobiusOverrides/GridContainer/GridContainer";
import { DivWrapper } from "@conklin/XDL/constants";
import { mergeWithCss, xClassNames, xScreen } from "@conklin/XDL/Utilities";
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
    // XNG-Changes
    rowId = "rowId",
    backgroundColor = "backgroundColor",
    makeItemsTakeFullHeight = "makeItemsTakeFullHeight",
    makeItemsTakeFullWidth = "makeItemsTakeFullWidth",
    horizontalAlignment = "horizontalAlignment",
    allowOverlapOn = "allowOverlapOn",
    overlapPosition = "overlapPosition",
    overlapValue = "overlapValue",
    selectOverlapItem = "selectOverlapItem",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.rowId]: string;
        [fields.backgroundColor]: string;
        [fields.makeItemsTakeFullHeight]: boolean;
        [fields.makeItemsTakeFullWidth]: boolean;
        [fields.horizontalAlignment]: HorizontalAlignment[];
        [fields.allowOverlapOn]: "wideScreen" | "both" | "none";
        [fields.overlapPosition]: string;
        [fields.overlapValue]: string;
        [fields.selectOverlapItem]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

// XNG-Changes: adding styles
interface RowStyles {
    container?: InjectableCss;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

const rowStyles: RowStyles = {
    container: {
        css: css`
            ${({ allowOverlapOn, backgroundColor, canOverlap, overlapPosition, overlapValue }: any) => {
                if (!canOverlap) {
                    return undefined;
                }

                let cssStyle: any = css``;
                let cssMobileStyle: any = css``;

                if (overlapPosition === "both") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            transparent 0%,
                            transparent ${(100 - overlapValue) / 2}%,
                            ${backgroundColor} ${(100 - overlapValue) / 2}%,
                            ${backgroundColor} ${overlapValue + (100 - overlapValue) / 2}%,
                            transparent ${overlapValue + (100 - overlapValue) / 2}%,
                            transparent 100%
                        );
                    `;
                }
                if (overlapPosition === "top") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            transparent 0%,
                            transparent ${100 - overlapValue}%,
                            ${backgroundColor} ${100 - overlapValue}%,
                            ${backgroundColor} 100%
                        );
                    `;
                }
                if (overlapPosition === "bottom") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            ${backgroundColor} ${overlapValue}%,
                            transparent ${overlapValue}%
                        );
                    `;
                }
                if (allowOverlapOn === "wideScreen") {
                    cssMobileStyle = css`
                        ${xScreen("lte", "sm")} {
                            background: ${backgroundColor};
                        }
                    `;
                }

                return css`
                    ${cssStyle}
                    ${cssMobileStyle}
                `;
            }}
        `,
    },
    gridItem: {
        css: css`
            ${({ makeItemsTakeFullHeight }: any) =>
                makeItemsTakeFullHeight &&
                css`
                    ${DivWrapper} {
                        height: 100%;
                    }
                `}
            ${({ horizontalAlign }: any) =>
                horizontalAlign === "center"
                    ? css`
                          display: flex;
                          justify-content: center;
                      `
                    : horizontalAlign === "right"
                    ? css`
                          display: flex;
                          justify-content: flex-end;
                      `
                    : horizontalAlign === "left" &&
                      css`
                          display: flex;
                          justify-content: flex-start;
                      `}
        `,
    },
};

// XNG-Changes: adding RowStyleWrapper
const RowStyleWrapper = styled.div<
    {
        backgroundColor?: string;
        canOverlap?: boolean;
        allowOverlapOn?: "wideScreen" | "both" | "none";
        overlapPosition?: string;
        overlapValue?: string;
        rowId?: string;
    } & InjectableCss<any>
>`
    ${({ backgroundColor }) =>
        backgroundColor &&
        css`
            background: ${backgroundColor};
        `}
    ${injectCss}
`;

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            isFullWidth,
            fullWidthStylingType,
            fullWidthColor,
            fullWidthImage,
            fullWidthImageFocalPoint,
            customCSS,
            // XNG-Changes
            rowId,
            backgroundColor,
            makeItemsTakeFullHeight,
            makeItemsTakeFullWidth,
            horizontalAlignment,
            allowOverlapOn,
            overlapPosition,
            overlapValue,
            selectOverlapItem,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];
    // XNG-Changes: adding overlap and styles
    const styles = rowStyles;
    const isAllowOverlap = allowOverlapOn !== "none";
    const rowRef = React.createRef<HTMLDivElement>();
    const canOverlap = useRowOverlap(rowRef, isAllowOverlap);

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        // XNG-Changes: adding horizontal alignment
        const horizontalAlign = horizontalAlignment[x] ? horizontalAlignment[x] : "";
        const props: { [key: string]: string | boolean } = {
            makeItemsTakeFullHeight,
            horizontalAlign,
        };

        // XNG-Changes: adding overlap
        const isOverlapItem =
            isAllowOverlap && (parseInt(selectOverlapItem, 10) - 1 === x || selectOverlapItem === "all");

        if (isOverlapItem) {
            props["data-overlap"] = overlapValue;
        }

        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                // XNG-Changes
                className={xClassNames.append(isOverlapItem ? "overlap" : "no-overlap")}
                {...props}
                {...styles.gridItem}
            >
                {/* XNG-Changes: use xngzone */}
                <XngZone
                    contentId={id}
                    zoneName={`Content${x}`}
                    hint={`Content-${x + 1}`}
                    normalWidth={!makeItemsTakeFullWidth}
                />
            </GridItem>,
        );
    }

    const fullWidthBackgroundCss = !isFullWidth
        ? undefined
        : fullWidthStylingType === "solidColor"
        ? css`
              background-color: ${fullWidthColor};
          `
        : fullWidthStylingType === "image"
        ? css`
              background-image: url("${parseImageUrl(fullWidthImage).url}");
              background-size: cover;
              ${getFocalPointStyles(fullWidthImageFocalPoint)}
          `
        : undefined;

    const finalCss = css`
        // XNG-Changes: replacing fullWidthBackgroundCss with overlap container css
        ${styles.container?.css}
        ${customCSS}
    `;

    return (
        // XNG-Changes: restructure & adding RowStyleWrapper
        <RowStyleWrapper
            ref={rowRef}
            backgroundColor={backgroundColor}
            canOverlap={canOverlap}
            allowOverlapOn={allowOverlapOn}
            overlapPosition={overlapPosition}
            overlapValue={overlapValue}
            data-test-selector="row-widget"
            css={finalCss}
            id={rowId}
        >
            <GridContainer
                gap={gap || 0}
                isFullWidth={isFullWidth}
                {...mergeWithCss(styles.gridContainer, { css: fullWidthBackgroundCss })}
                css={finalCss}
                offsetCss={css`
                    ${isAllowOverlap && `align-items: center;`}
                `}
            >
                {columnElements}
            </GridContainer>
        </RowStyleWrapper>
    );
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

// XNG-Changes: adding options tab
const optionsTab = {
    displayName: "Options",
    sortOrder: 2,
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
                sortOrder: 3,
            },
            {
                name: fields.fullWidthStylingType,
                displayName: "Styling",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Solid Color",
                        value: "solidColor",
                    },
                    {
                        displayName: "Image",
                        value: "image",
                    },
                ],
                defaultValue: "solidColor",
                horizontal: true,
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth,
                sortOrder: 4,
            },
            {
                name: fields.fullWidthColor,
                displayName: "Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "solidColor",
                sortOrder: 5,
            },
            {
                name: fields.fullWidthImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
                sortOrder: 6,
            },
            {
                name: fields.fullWidthImageFocalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                fieldType: "General",
                hideEmptyOption: true,
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
                sortOrder: 7,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                tab: advancedTab,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                sortOrder: 4,
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes: adding horizontal alignment
            {
                name: fields.horizontalAlignment,
                displayName: "Horizontal Alignment",
                editorTemplate: "HorizontalAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
                tab: advancedTab,
            },
            // XNG-Changes: adding background color
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            // XNG-Changes: adding make columns same height
            {
                name: fields.makeItemsTakeFullHeight,
                displayName: "Show all items with the same height",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: optionsTab,
            },
            // XNG-Changes: adding make columns same width
            {
                name: fields.makeItemsTakeFullWidth,
                displayName: "Show all items with the same width",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.allowOverlapOn,
                displayName: "Allow Overlap",
                editorTemplate: "DropDownField",
                defaultValue: "none",
                hideEmptyOption: true,
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Wide Screen Only",
                        value: "wideScreen",
                    },
                ],
                fieldType: "General",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.overlapPosition,
                displayName: "Overlap side (from)",
                editorTemplate: "DropDownField",
                hideEmptyOption: true,
                fieldType: "General",
                options: [
                    { displayName: "top", value: "top" },
                    { displayName: "both", value: "both" },
                    { displayName: "bottom", value: "bottom" },
                ],
                defaultValue: "both",
                isRequired: true,
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.overlapValue,
                displayName: "Overlap Value %",
                editorTemplate: "IntegerField",
                defaultValue: 70,
                max: 100,
                isRequired: true,
                fieldType: "General",
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.selectOverlapItem,
                displayName: "Select Overlap Column",
                editorTemplate: "SelectOverlapItemTemplate",
                defaultValue: "all",
                hideEmptyOption: true,
                fieldType: "General",
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: optionsTab,
            },
            // XNG-Changes: adding row id
            {
                name: fields.rowId,
                displayName: "Row Id",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
